import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { FileText, Shield, Users, Lock, Scale, CheckCircle, Info } from 'lucide-react';

const ConditionsGeneralesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#mediation-consommation') {
      document.getElementById('mediation-consommation')?.scrollIntoView({ block: 'start' });
    }
  }, [location.hash]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navigation />
      
      {/* Bouton Retour */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackButton to="/" label="Retour à l'accueil" />
      </div>

      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-indigo-900 mb-4 text-center">
            Conditions Générales d'Utilisation (CGU)
          </h1>
          <p className="text-center text-gray-600 mb-2">SCIC SAS OBJECTIF SCOP OUTREMER (OSO)</p>
          <p className="text-center text-sm text-gray-500 mb-8">Mise à jour : septembre 2026</p>
          
          <Card className="shadow-xl">
            <CardContent className="p-4 md:p-8">
              <div className="prose prose-lg max-w-none">
                
                {/* Section 1 - Objet */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <FileText className="h-6 w-6 text-amber-500" />
                    1. OBJET DES CGU
                  </h2>
                  <p className="mb-4">
                    Les présentes Conditions Générales d'Utilisation ont pour objet d'encadrer l'accès et l'utilisation du site Internet édité par :
                  </p>
                  <div className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-600 mb-4">
                    <p className="font-semibold text-indigo-900 mb-2">SCIC SAS OBJECTIF SCOP OUTREMER</p>
                    <p>SIREN : <strong>903 459 139</strong></p>
                    <p>Siège social : <strong>13 rue Rodrigue YOUYOUTE, 97139 Les Abymes – Guadeloupe</strong></p>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-blue-900 mb-2">Le site a pour finalité :</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700">
                      <li>d'informer sur les activités coopératives, solidaires et territoriales de la SCIC OSO</li>
                      <li>de permettre l'accès à des espaces dédiés (membres, conseillers, experts)</li>
                      <li>de présenter les dispositifs ESS liés au groupe coopératif (OSO / COPPAM / FOGEDOM-SCIC)</li>
                      <li>de faciliter la mise en relation ou l'accompagnement ESS des entreprises ultramarines</li>
                    </ul>
                  </div>
                  <p className="mt-4 text-sm italic text-gray-600">Les CGU constituent un accord entre l'utilisateur et l'éditeur du site.</p>
                </div>

                {/* Section 2 - Acceptation */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <CheckCircle className="h-6 w-6 text-amber-500" />
                    2. ACCEPTATION DES CONDITIONS
                  </h2>
                  <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
                    <p className="font-medium">En accédant au site, l'utilisateur reconnaît avoir pris connaissance des CGU et les accepter sans réserve.</p>
                    <p className="mt-2 text-sm text-amber-900">Si l'utilisateur n'accepte pas tout ou partie des présentes conditions, il doit cesser toute utilisation du site.</p>
                  </div>
                </div>

                {/* Section 3 - Accès */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-4">
                    3. ACCÈS AU SITE
                  </h2>
                  <p className="mb-3">Le site est accessible gratuitement à tout utilisateur disposant d'un accès Internet.</p>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-sm text-gray-700">OSO s'efforce d'assurer l'accessibilité du site, mais ne garantit aucune disponibilité permanente. Des interruptions sont possibles pour maintenance, mise à jour ou circonstances extérieures.</p>
                  </div>
                </div>

                {/* Section 3bis - Condition d'accès aux dispositifs et services */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <Shield className="h-6 w-6 text-amber-500" />
                    3bis. CONDITION D'ACCÈS AUX DISPOSITIFS ET SERVICES
                  </h2>
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg border-l-4 border-indigo-600 mb-4">
                    <p className="mb-4 text-gray-800">
                      L'adhésion à la <strong className="text-indigo-700">FTPE Outremer</strong> constitue le cadre institutionnel
                      requis pour accéder aux dispositifs solidaires portés par <strong className="text-indigo-700">COPPAM</strong> ainsi qu'aux services proposés au sein de l'écosystème <strong className="text-indigo-700">O'SCOP</strong>.
                    </p>
                    <p className="text-gray-800">
                      Chaque garantie ou service reste soumis à ses conditions propres,
                      à une instruction dédiée et, le cas échéant, à une contribution spécifique.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-sm font-medium">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Cadre institutionnel requis
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                      <Info className="w-4 h-4 mr-2" />
                      Conditions spécifiques par service
                    </span>
                  </div>
                </div>

                {/* Section 4 - Services */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-4">
                    4. SERVICES PROPOSÉS SUR LE SITE
                  </h2>
                  <p className="mb-3">Le site peut proposer selon les espaces accessibles :</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <div className="bg-blue-50 p-3 rounded border-l-2 border-blue-400">
                      <p className="text-sm">✔ Informations institutionnelles sur OSO</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded border-l-2 border-blue-400">
                      <p className="text-sm">✔ Présentation des établissements secondaires</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded border-l-2 border-blue-400">
                      <p className="text-sm">✔ Présentation des actions ESS (COPPAM / FOGEDOM)</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded border-l-2 border-blue-400">
                      <p className="text-sm">✔ Accès à un espace membre</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded border-l-2 border-blue-400">
                      <p className="text-sm">✔ Accès à un espace expert / conseiller ESS</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded border-l-2 border-blue-400">
                      <p className="text-sm">✔ Formulaires de contact et d'inscription</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 p-4 rounded border-l-4 border-amber-500 mt-4">
                    <p className="text-sm font-semibold text-amber-900">Les parcours de souscription, de paiement ou de demande de prestation accessibles sur le site sont régis par leurs conditions contractuelles propres.</p>
                  </div>
                </div>

                {/* Section 5 - Espaces réservés */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <Lock className="h-6 w-6 text-amber-500" />
                    5. ESPACES RÉSERVÉS
                  </h2>
                  <p className="mb-3">Certains espaces du site sont accessibles uniquement aux :</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                    <li>membres adhérents</li>
                    <li>conseillers ESS</li>
                    <li>partenaires coopératifs</li>
                    <li>experts techniques FOGEDOM-SCIC</li>
                    <li>structures du groupe ESS</li>
                  </ul>
                  <div className="bg-red-50 p-4 rounded border-l-4 border-red-500">
                    <p className="text-sm font-medium text-red-900">Les accès sont strictement personnels et non transférables. Toute utilisation non autorisée d'un compte peut entraîner sa suspension.</p>
                  </div>
                </div>

                {/* Section 6 - Responsabilités */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <Shield className="h-6 w-6 text-amber-500" />
                    6. RESPONSABILITÉS
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold text-indigo-800 mb-2">6.1 Responsabilité de l'utilisateur</h3>
                      <p className="mb-2">L'utilisateur s'engage à :</p>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700">
                        <li>ne pas altérer le fonctionnement du site</li>
                        <li>ne pas accéder à des espaces non autorisés</li>
                        <li>ne pas transmettre de contenu illicite</li>
                        <li>fournir des informations exactes lors de l'utilisation des formulaires</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-indigo-800 mb-2">6.2 Responsabilité de l'éditeur</h3>
                      <p className="mb-2">La SCIC OSO n'est responsable que du contenu qu'elle édite.</p>
                      <p className="mb-2">Elle ne peut être tenue responsable :</p>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700">
                        <li>d'indisponibilités du site</li>
                        <li>d'atteintes liées au réseau Internet</li>
                        <li>de fausses informations provenant de sites tiers</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 7 - Propriété intellectuelle */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-4">
                    7. PROPRIÉTÉ INTELLECTUELLE
                  </h2>
                  <div className="bg-indigo-50 p-4 rounded border-l-4 border-indigo-600">
                    <p className="mb-3">Le site, son contenu, ses logos, textes, documents, supports de formation et éléments graphiques sont protégés par le droit d'auteur.</p>
                    <p className="font-semibold text-indigo-900">Toute reproduction ou exploitation non autorisée est interdite.</p>
                  </div>
                </div>

                {/* Section 8 - RGPD */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <Shield className="h-6 w-6 text-amber-500" />
                    8. DONNÉES PERSONNELLES – RGPD
                  </h2>
                  <p className="mb-4">OSO traite les données personnelles conformément au RGPD.</p>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded">
                      <h3 className="font-semibold text-indigo-900 mb-2">8.1 Responsable du traitement</h3>
                      <p>SCIC SAS OBJECTIF SCOP OUTREMER</p>
                      <p>Email : <a href="mailto:dpo@objectifscopoutremer.com" className="text-indigo-600 hover:text-amber-500 font-medium">dpo@objectifscopoutremer.com</a></p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded">
                      <h3 className="font-semibold text-indigo-900 mb-2">8.2 Finalités</h3>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                        <li>gestion des comptes utilisateurs</li>
                        <li>gestion des demandes d'accompagnement</li>
                        <li>accès aux espaces réservés</li>
                        <li>relations coopératives</li>
                        <li>statistiques d'amélioration du service</li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded">
                      <h3 className="font-semibold text-indigo-900 mb-2">8.3 Droits des utilisateurs</h3>
                      <p className="mb-2 text-sm">Chaque utilisateur dispose des droits : d'accès, de rectification, d'effacement, de limitation, d'opposition, et de portabilité.</p>
                      <p className="text-sm">Toute demande doit être adressée à : <a href="mailto:dpo@objectifscopoutremer.com" className="text-indigo-600 hover:text-amber-500 font-medium">dpo@objectifscopoutremer.com</a></p>
                    </div>
                  </div>
                </div>

                {/* Section 9 - Cookies */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-4">
                    9. COOKIES
                  </h2>
                  <p className="mb-3">Le site peut utiliser des cookies techniques, analytiques ou fonctionnels.</p>
                  <p className="text-sm text-gray-600">L'utilisateur peut configurer ses choix via le bandeau cookies.</p>
                </div>

                {/* Section 10 - Relations OSO/COPPAM/FOGEDOM */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <Users className="h-6 w-6 text-amber-500" />
                    10. RELATION OSO / COPPAM / FOGEDOM-SCIC
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="bg-indigo-50 p-4 rounded border-l-4 border-indigo-600">
                      <h3 className="font-semibold text-indigo-900 mb-2">10.1 Rôle de OSO (Éditeur du site)</h3>
                      <p className="text-sm">OSO héberge : l'activité économique coopérative, la plateforme numérique, et les relations administratives avec les membres.</p>
                    </div>

                    <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-600">
                      <h3 className="font-semibold text-blue-900 mb-2">10.2 Rôle de COPPAM</h3>
                      <p className="text-sm mb-2">COPPAM n'est pas l'éditeur du site et n'exerce aucune activité commerciale.</p>
                      <p className="text-sm font-medium">COPPAM intervient exclusivement dans un cadre ESS de solidarité, prévention, médiation et accompagnement civil.</p>
                    </div>

                    <div className="bg-green-50 p-4 rounded border-l-4 border-green-600">
                      <h3 className="font-semibold text-green-900 mb-2">10.3 Rôle de FOGEDOM-SCIC</h3>
                      <p className="text-sm mb-2">Le Fonds interne FOGEDOM-SCIC intervient uniquement pour : l'analyse technique, l'accompagnement solidaire, et les actions coopératives internes.</p>
                      <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded">
                        <p className="text-xs font-semibold text-amber-900">⚠️ FOGEDOM-SCIC n'est pas une assurance et ne fournit aucune prestation assurantielle.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 11 - Hyperliens */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-4">
                    11. HYPERLIENS
                  </h2>
                  <p className="text-sm text-gray-700">Le site peut contenir des liens vers des sites tiers. OSO décline toute responsabilité concernant leur contenu.</p>
                </div>

                {/* Section 12 - Modification */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-4">
                    12. MODIFICATION DES CGU
                  </h2>
                  <div className="bg-blue-50 p-4 rounded">
                    <p className="text-sm">OSO peut modifier à tout moment les présentes CGU. Les utilisateurs seront informés en cas de modification substantielle.</p>
                  </div>
                </div>

                {/* Section 13 - Droit applicable */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                    <Scale className="h-6 w-6 text-amber-500" />
                    13. DROIT APPLICABLE
                  </h2>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="mb-2">Les CGU sont soumises au droit français.</p>
                    <p className="text-sm text-gray-700">En cas de litige, les juridictions compétentes sont déterminées conformément aux règles applicables, sans préjudice des droits du consommateur.</p>
                  </div>
                </div>

                {/* Section 14 - Médiation de la consommation */}
                  <div id="mediation-consommation" className="mb-8 pb-6 border-b border-gray-200 scroll-mt-24">
                    <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                      <Scale className="h-6 w-6 text-amber-500" />
                      14. MÉDIATION DE LA CONSOMMATION
                    </h2>
                    <div className="bg-gray-50 p-4 rounded space-y-3 text-sm text-gray-700">
                      <p>Pour tout litige de consommation relatif à un contrat de vente ou à une prestation de services, le consommateur doit d'abord adresser une réclamation à O'SCOP, par courriel à <a className="text-indigo-600 underline" href="mailto:contact@objectifscopoutremer.com">contact@objectifscopoutremer.com</a> ou par courrier au 13 rue Rodrigue YOUYOUTE, 97139 Les Abymes, Guadeloupe.</p>
                      <p>Si cette réclamation n'aboutit pas, il peut recourir gratuitement à la médiation de la consommation auprès d'<strong>ANM CONSOMMATION</strong>, conformément aux articles L. 611-1 et suivants et R. 612-1 et suivants du Code de la consommation.</p>
                      <p>La saisine s'effectue en ligne sur <a className="text-indigo-600 underline" href="https://www.anmconso.com" target="_blank" rel="noopener noreferrer">www.anmconso.com</a> ou par courrier à ANM CONSOMMATION, 25 allée Rose Dieng Kuntz, 75019 Paris. Pour tout renseignement : 01 58 64 00 05, du lundi au vendredi de 9 h à 12 h.</p>
                      <p>Pour les litiges transfrontaliers, des informations sont disponibles sur <a className="text-indigo-600 underline" href="https://www.economie.gouv.fr/mediation-conso" target="_blank" rel="noopener noreferrer">economie.gouv.fr/mediation-conso</a>.</p>
                    </div>
                  </div>

                  {/* Section 15 - Contact */}
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-4">
                    15. CONTACT
                  </h2>
                  <div className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-600">
                    <p className="font-semibold text-indigo-900 mb-3">Pour toute question relative au site ou aux CGU :</p>
                    <p className="flex items-center gap-2">
                      <span>📧</span>
                      <a href="mailto:contact@objectifscopoutremer.com" className="text-indigo-600 hover:text-amber-500 font-medium">contact@objectifscopoutremer.com</a>
                    </p>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ConditionsGeneralesPage;
